import React from "react";
import { DateTime } from "luxon";
import AutoComplete from "../../utils/components/AutoCompleteComp/AutoComplete2.tsx";
import DateInput from "../../utils/components/DateInput.tsx";
import FloatInput from "../../utils/components/FloatInput.tsx";
import { trpc } from "../../main/components/ProviderContainer.tsx";
import { filterCompte } from "../processing/compteSearcher";
import modes from "../../main/consts/modes.ts";
import type { FactureDraft, FactureDuplicateCandidate } from "compta-shared";
import DuplicateFactureWarningModal, { getDuplicateConfirmationKeys } from "./DuplicateFactureWarningModal.tsx";
import ConfirmModal from "./ConfirmModal.tsx";
import LockedBadge from "../../utils/components/LockedBadge.tsx";
import { ArrowReturnLeft } from "react-bootstrap-icons";

type InvoiceType = "normal" | "resto";
type InvoiceMode = (typeof modes)[number];

export type FactureDraftItem = Pick<
  FactureDraft,
  | "_id"
  | "source"
  | "libellé"
  | "invoiceType"
  | "modeRef"
  | "chequeNumber"
  | "montantCent"
  | "dateFacture"
  | "datePrelevement"
  | "paymentDueDate"
  | "montantTitresCent"
  | "montantFraisCent"
  | "originalFileName"
  | "sourceEmailSender"
  | "sourceEmailSubject"
  | "restoredAccountingData"
  | "createdAt"
  | "locked"
  | "reviewMode"
  | "reviewCompte"
> & {
  pdfUrl: string;
  suggestedCompte: string;
  suggestedMode: InvoiceMode;
};

interface FinalizeDraftPanelProps {
  draft: FactureDraftItem;
  onClose: () => void;
  onCreated: () => void;
}

const formatCreatedAt = (date: Date) => {
  return DateTime.fromJSDate(date).toFormat("dd/MM/yyyy HH:mm");
};

const isInvoiceType = (value: string): value is InvoiceType => {
  return value === "normal" || value === "resto";
};

const isInvoiceMode = (value: string): value is InvoiceMode => {
  return modes.some((mode) => mode === value);
};

const FinalizeDraftPanel = ({ draft, onClose, onCreated }: FinalizeDraftPanelProps) => {
  const utils = trpc.useUtils();
  const isLocked = draft.locked === true;
  const { data: libellés } = trpc.libellé.getAll.useQuery();
  const finalizeMutation = trpc.facture.finalizeDraft.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.facture.invalidate(),
        utils.superPdp.invalidate(),
        utils.libellé.invalidate(),
      ]);
      onCreated();
    },
  });
  const rejectMutation = trpc.facture.rejectDraft.useMutation({
    onSuccess: async () => {
      await utils.facture.invalidate();
      onClose();
    },
  });
  const returnMutation = trpc.facture.returnToReview.useMutation({
    onSuccess: async () => {
      await Promise.all([utils.facture.invalidate(), utils.superPdp.invalidate()]);
      onClose();
    },
  });

  const [emetteurSearch, setEmetteurSearch] = React.useState("");
  const [libellé, setLibellé] = React.useState(draft.libellé);
  const [invoiceType, setInvoiceType] = React.useState<InvoiceType>(draft.invoiceType);
  const [modeSearch, setModeSearch] = React.useState("");
  const [mode, setMode] = React.useState<InvoiceMode>(draft.reviewMode || draft.suggestedMode);
  const [compteSearch, setCompteSearch] = React.useState("");
  const [compte, setCompte] = React.useState(
    draft.restoredAccountingData
      ? draft.restoredAccountingData.compte || ""
       : draft.reviewCompte ?? draft.suggestedCompte,
  );
  const [modeRef, setModeRef] = React.useState(draft.modeRef);
  const [chequeNumber, setChequeNumber] = React.useState(draft.chequeNumber || "");
  const [montantCent, setMontantCent] = React.useState<number | null>(draft.montantCent);
  const [dateFacture, setDateFacture] = React.useState<Date | null>(draft.dateFacture);
  const [datePrelevement, setDatePrelevement] = React.useState<Date | null>(
    draft.datePrelevement ?? DateTime.now().startOf("day").toJSDate(),
  );
  const [montantTitresCent, setMontantTitresCent] = React.useState<number | null>(
    draft.montantTitresCent,
  );
  const [montantFraisCent, setMontantFraisCent] = React.useState<number | null>(
    draft.montantFraisCent,
  );
  const [duplicateCandidates, setDuplicateCandidates] = React.useState<
    FactureDuplicateCandidate[]
  >([]);
  const [duplicateCheckError, setDuplicateCheckError] = React.useState<string | null>(
    null,
  );
  const [showReturnConfirm, setShowReturnConfirm] = React.useState(false);

  const emetteurCandidates = emetteurSearch
    ? (libellés || [])
        .map((item) => item.libellé)
        .filter((label) => new RegExp(emetteurSearch, "i").test(label))
    : [];

  const modeCandidates = modeSearch
    ? modes.filter((candidate) => new RegExp(modeSearch, "i").test(candidate))
    : [];

  const compteCandidates = filterCompte(compteSearch);

  const handleEmetteurChange = (value: string) => {
    const nextValue = value || "";
    const matchingLibellé = libellés?.find((item) => item.libellé === nextValue);
    setLibellé(nextValue);
    if (!draft.restoredAccountingData && matchingLibellé?.compte) {
      setCompte(matchingLibellé.compte);
      setCompteSearch("");
    }
  };

  const blockingErrors: string[] = [];
  if (!libellé.trim()) {
    blockingErrors.push("Émetteur obligatoire");
  }
  if (!modeRef.trim()) {
    blockingErrors.push("Référence facture obligatoire");
  }
  if (typeof montantCent !== "number") {
    blockingErrors.push("Montant TTC obligatoire");
  }
  if (!dateFacture && !datePrelevement) {
    blockingErrors.push("Date facture ou date de réglement obligatoire");
  }
  if (
    invoiceType === "normal" &&
    !draft.restoredAccountingData &&
    !compte.trim()
  ) {
    blockingErrors.push("Compte comptable obligatoire");
  }
  if (!mode) {
    blockingErrors.push("Mode de règlement obligatoire");
  }
  if (
    invoiceType === "resto" &&
    (typeof montantTitresCent !== "number" || typeof montantFraisCent !== "number")
  ) {
    blockingErrors.push("Montants titres/frais obligatoires pour une facture resto");
  }

  const commitFinalize = (confirmedDuplicateKeys: string[] = []) => {
    if (isLocked || typeof montantCent !== "number") {
      return;
    }
    finalizeMutation.mutate({
      draftId: draft._id,
      libellé: libellé.trim(),
      invoiceType,
      mode,
      compte: invoiceType === "normal" ? compte.trim() : null,
      modeRef: modeRef.trim(),
      chequeNumber:
        mode === "Chèque1"
          ? (chequeNumber || "").trim() || modeRef.trim()
          : null,
      montantCent,
      dateFacture,
      datePrelevement,
      montantTitresCent,
      montantFraisCent,
      useRestoredAccountingData: !!draft.restoredAccountingData,
      confirmedDuplicateKeys,
    });
  };

  const handleFinalize = async () => {
    if (
      isLocked ||
      blockingErrors.length > 0 ||
      typeof montantCent !== "number"
    ) {
      return;
    }
    setDuplicateCheckError(null);
    try {
      const duplicates = await utils.facture.checkBusinessDuplicates.fetch({
        modeRef: modeRef.trim(),
        date: dateFacture,
        datePrelevement,
        montantCent,
        excludeDraftId: draft._id,
      });
      if (duplicates.length > 0) {
        setDuplicateCandidates(duplicates);
        return;
      }
      commitFinalize();
    } catch (error) {
      setDuplicateCheckError(
        error instanceof Error
          ? error.message
          : "Erreur pendant la recherche de doublons",
      );
    }
  };

  return (
    <div className="card mt-4">
      <div className="card-header d-flex justify-content-between align-items-center gap-3">
        <div>
          <div className="fw-bold">Enregistrer la facture</div>
          {isLocked ? <LockedBadge /> : null}
          <div className="text-muted small">
            {draft.originalFileName} • créé le {formatCreatedAt(draft.createdAt)}
          </div>
          {draft.sourceEmailSender || draft.sourceEmailSubject ? (
            <div className="text-muted small">
              {draft.sourceEmailSender || "Expéditeur inconnu"}
              {draft.sourceEmailSubject ? ` • ${draft.sourceEmailSubject}` : ""}
            </div>
          ) : null}
        </div>
        <div className="d-flex gap-2">
          <a
            className="btn btn-outline-secondary btn-sm"
            href={draft.pdfUrl}
            target="_blank"
            rel="noreferrer"
          >
            Voir le PDF
          </a>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>
            Fermer
          </button>
        </div>
      </div>
      <div className="card-body">
        <fieldset className="m-0 p-0 border-0" disabled={isLocked}>
        <div className="row g-3">
            <div className="col-12 col-xl-7">
              {draft.restoredAccountingData ? (
                <div className="alert alert-info">
                  Le compte et les ventilations de la facture d'origine seront
                  conservés.
                </div>
              ) : null}
              <div className="row g-3">
              <div className="col-12 col-md-4">
                <label className="form-label">Type</label>
                <select
                  className="form-select"
                  value={invoiceType}
                  disabled={!!draft.restoredAccountingData}
                  onChange={(event) => {
                    const nextValue = event.currentTarget.value;
                    if (isInvoiceType(nextValue)) {
                      setInvoiceType(nextValue);
                    }
                  }}
                >
                  <option value="normal">Facture normale</option>
                  <option value="resto">Facture resto</option>
                </select>
              </div>
              <div className="col-12 col-md-4">
                <label className="form-label">Mode</label>
                <AutoComplete
                  candidates={modeCandidates}
                  onSetSearch={(value) => setModeSearch(value || "")}
                  onSetValue={(value) => {
                    if (isInvoiceMode(value)) {
                      setMode(value);
                    }
                  }}
                  value={mode}
                  tagsOn={false}
                  acceptNewValues={false}
                  isValid={!!mode}
                  invalidMessage="Mode obligatoire"
                  isTouched={true}
                />
              </div>
              <div className="col-12 col-md-4">
                <label className="form-label">Échéance</label>
                <input
                  className="form-control"
                  value={
                    draft.paymentDueDate
                      ? DateTime.fromJSDate(draft.paymentDueDate).toFormat("dd/MM/yyyy")
                      : "-"
                  }
                  readOnly
                />
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label">Émetteur</label>
                <AutoComplete
                  candidates={emetteurCandidates}
                  onSetSearch={(value) => setEmetteurSearch(value || "")}
                  onSetValue={handleEmetteurChange}
                  value={libellé}
                  tagsOn={false}
                  acceptNewValues={true}
                  isValid={!!libellé.trim()}
                  invalidMessage="Émetteur obligatoire"
                  isTouched={true}
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Compte</label>
                {draft.restoredAccountingData ? (
                  <input
                    className="form-control"
                    value={compte || "Ventilation générale"}
                    readOnly
                  />
                ) : (
                  <AutoComplete
                    candidates={compteCandidates}
                    onSetSearch={(value) => setCompteSearch(value || "")}
                    onSetValue={(value) => setCompte(value || "")}
                    value={compte}
                    tagsOn={false}
                    acceptNewValues={false}
                    isValid={invoiceType === "resto" || !!compte.trim()}
                    invalidMessage="Compte obligatoire pour une facture normale"
                    isTouched={true}
                  />
                )}
              </div>

              <div className="col-12 col-md-4">
                <label className="form-label">Montant TTC</label>
                {draft.restoredAccountingData ? (
                  <FloatInput valueCent={montantCent} disabled={true} />
                ) : (
                  <FloatInput
                    valueCent={montantCent}
                    onValueCentChange={setMontantCent}
                  />
                )}
              </div>
              <div className="col-12 col-md-4">
                <label className="form-label">Date facture</label>
                <DateInput date={dateFacture} onDateChange={setDateFacture} />
              </div>
              <div className="col-12 col-md-4">
                <label className="form-label">Date de réglement</label>
                <DateInput date={datePrelevement} onDateChange={setDatePrelevement} />
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label">Référence facture</label>
                <input
                  type="text"
                  className="form-control"
                  value={modeRef}
                  onChange={(event) => setModeRef(event.currentTarget.value)}
                />
              </div>
              {mode === "Chèque1" ? (
                <div className="col-12 col-md-6">
                  <label className="form-label">Numéro de chèque</label>
                  <input
                    type="text"
                    className="form-control"
                    value={chequeNumber}
                    onChange={(event) => setChequeNumber(event.currentTarget.value)}
                  />
                </div>
              ) : null}

              {invoiceType === "resto" ? (
                <>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Montant titres</label>
                    <FloatInput
                      valueCent={montantTitresCent}
                      onValueCentChange={setMontantTitresCent}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Montant frais</label>
                    <FloatInput
                      valueCent={montantFraisCent}
                      onValueCentChange={setMontantFraisCent}
                    />
                  </div>
                </>
              ) : null}
            </div>

            {finalizeMutation.error ? (
              <div className="alert alert-danger mt-3 mb-0" role="alert">
                {finalizeMutation.error.message}
              </div>
            ) : null}
            {duplicateCheckError ? (
              <div className="alert alert-danger mt-3 mb-0" role="alert">
                {duplicateCheckError}
              </div>
            ) : null}
            {rejectMutation.error ? (
              <div className="alert alert-danger mt-3 mb-0" role="alert">
                {rejectMutation.error.message}
              </div>
            ) : null}
            {returnMutation.error ? (
              <div className="alert alert-danger mt-3 mb-0" role="alert">
                {returnMutation.error.message}
              </div>
            ) : null}
            {blockingErrors.length > 0 ? (
              <div className="alert alert-warning mt-3 mb-0" role="alert">
                {blockingErrors.join(" • ")}
              </div>
            ) : null}

            <div className="d-flex gap-2 mt-3">
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={
                  isLocked ||
                  returnMutation.isPending ||
                  rejectMutation.isPending ||
                  finalizeMutation.isPending
                }
                onClick={() => {
                  if (!isLocked) {
                    setShowReturnConfirm(true);
                  }
                }}
              >
                <ArrowReturnLeft className="me-1" /> Remettre à traiter
              </button>
              <button
                type="button"
                className="btn btn-outline-danger"
                disabled={
                  isLocked ||
                  returnMutation.isPending ||
                  rejectMutation.isPending ||
                  finalizeMutation.isPending
                }
                onClick={() => {
                  if (!isLocked) {
                    rejectMutation.mutate(draft._id);
                  }
                }}
              >
                {rejectMutation.isPending ? "Rejet..." : "Rejeter"}
              </button>
              <button
                type="button"
                className="btn btn-success"
                disabled={
                  isLocked ||
                  blockingErrors.length > 0 ||
                  returnMutation.isPending ||
                  finalizeMutation.isPending
                }
                onClick={handleFinalize}
              >
                {finalizeMutation.isPending ? "Enregistrement..." : "Enregistrer la facture"}
              </button>
            </div>
          </div>

          <div className="col-12 col-xl-5">
            <div className="ratio ratio-4x3 border rounded overflow-hidden">
              <iframe title={`draft-pdf-${draft._id}`} src={draft.pdfUrl} />
            </div>
          </div>
        </div>
        </fieldset>
      </div>
      <DuplicateFactureWarningModal
        show={!isLocked && duplicateCandidates.length > 0}
        items={
          duplicateCandidates.length > 0
            ? [{ title: draft.originalFileName, duplicates: duplicateCandidates }]
            : []
        }
        onCancel={() => setDuplicateCandidates([])}
        onConfirm={() => {
          setDuplicateCandidates([]);
          commitFinalize(getDuplicateConfirmationKeys(duplicateCandidates));
        }}
        confirmLabel="Enregistrer la facture quand même"
        isSubmitting={finalizeMutation.isPending}
      />
      <ConfirmModal
        show={!isLocked && showReturnConfirm}
        title="Remettre cette facture à traiter ?"
        onCancel={() => { if (!returnMutation.isPending) setShowReturnConfirm(false); }}
        onConfirm={() => {
          if (!isLocked) {
            returnMutation.mutate({
              draftId: draft._id,
              changes: {
                libellé, invoiceType, modeRef, chequeNumber: chequeNumber.trim() || null,
                montantCent: montantCent ?? draft.montantCent, dateFacture, datePrelevement,
                montantTitresCent, montantFraisCent, reviewMode: mode, reviewCompte: compte,
              },
            });
          }
        }}
        confirmLabel="Remettre à traiter"
        submittingLabel="Remise à traiter..."
        isSubmitting={returnMutation.isPending}
      >
        <p className="mb-0">
          Cette facture sera retirée des règlements en attente. Vous pourrez reprendre son traitement
          dans « {draft.source === "superpdp" ? "Factures électroniques" : "À traiter"} ».
          Son PDF et les informations déjà renseignées seront conservés.
        </p>
      </ConfirmModal>
    </div>
  );
};

export default FinalizeDraftPanel;
